import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Typography, Box, Grid, Paper, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  InputAdornment, TableContainer, Skeleton, CircularProgress, Tooltip
} from '@mui/material';
import {
  Search as SearchIcon, TrendingDown, TrendingUp
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import Autocomplete from '@mui/material/Autocomplete';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import api from '../../../../services/api';

// ========= helpers =========
const formatBRL = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  try {
    return Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch {
    return `R$ ${Number(n).toFixed(2)}`;
  }
};
const formatPerc = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toFixed(2)}%`;
};

// Pequeno cartão KPI
const KpiCard = ({ label, value, isMoney = true, loading = false, accent = 'primary' }) => (
  <Paper
    component={motion.div}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25 }}
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 3,
      backdropFilter: 'blur(8px)',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.6))',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: .6 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
      {loading ? <Skeleton width={120} /> : (isMoney ? formatBRL(value) : formatPerc(value))}
    </Typography>
  </Paper>
);

const Dashboard = () => {
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
    faturamento: '',
    cmvMeta: '',
    insumo: null,
  });

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);

  // Autocomplete de insumos
  const [insumoOptions, setInsumoOptions] = useState([]);
  const [insumosQuery, setInsumosQuery] = useState('');
  const [insumosLoading, setInsumosLoading] = useState(false);

  // ===== fetch =====
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...(filters.dateFrom ? { date_from: format(filters.dateFrom, 'yyyy-MM-dd') } : {}),
        ...(filters.dateTo ? { date_to: format(filters.dateTo, 'yyyy-MM-dd') } : {}),
        ...(filters.faturamento ? { faturamento: filters.faturamento } : {}),
      };
      const { data } = await api.get('/confereai/relatorios/cmv-real', { params });
      setSummary({
        estoqueInicial: data.estoque_inicial,
        entradas: data.entradas,
        estoqueFinal: data.estoque_final,
        saidas: data.saidas,
        cmvPercentual: data.cmv_percentual,
      });
      setItems(Array.isArray(data?.itens) ? data.itens : []);
    } catch (err) {
      console.error('Erro ao carregar relatório de CMV real', err);
      setSummary(null);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters.dateFrom, filters.dateTo, filters.faturamento]);

  useEffect(() => {
    fetchReport(); /* on mount */
  }, [fetchReport]);

  // debounce insumos
  useEffect(() => {
    const t = setTimeout(async () => {
      if (insumosQuery === '') return;
      setInsumosLoading(true);
      try {
        const { data } = await api.get('/confereai/insumos', {
          params: { q: insumosQuery, per_page: 12 }
        });
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setInsumoOptions(list);
      } catch (err) {
        console.error('Erro ao buscar insumos', err);
        setInsumoOptions([]);
      } finally {
        setInsumosLoading(false);
      }
    }, 320);
    return () => clearTimeout(t);
  }, [insumosQuery]);

  const handleFilterChange = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));

  const displayedItems = useMemo(() => {
    if (!filters.insumo) return items;
    const id = filters.insumo.id;
    return items.filter((i) => i.id === id || i.insumo_id === id);
  }, [items, filters.insumo]);

  // meta vs real
  const metaNumber = filters.cmvMeta ? Number(filters.cmvMeta) : null;
  const acimaMeta = metaNumber != null && summary?.cmvPercentual != null
    ? Number(summary.cmvPercentual) > metaNumber
    : null;

  return (
    <Box sx={{ px: { xs: 3, sm: 5 }, pb: 4 }}>
      {/* Ajuste global simples do react-datepicker (setas) */}
      <style>{`
        .react-datepicker__navigation { top: 8px !important; }
      `}</style>

      {/* Header */}
      <Box
        sx={{
          mb: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: 0.3
          }}
        >
          CMV Real
        </Typography>

        <Button variant="contained" onClick={fetchReport}>
          Recalcular
        </Button>
      </Box>

      {/* Filtros */}
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(250,250,250,0.7))',
          backdropFilter: 'blur(8px)'
        }}
      >
        <Grid container spacing={2.5} alignItems="center">
          <Grid item xs={12} md={3}>
            <DatePicker
              selected={filters.dateFrom}
              onChange={(d) => handleFilterChange('dateFrom', d)}
              customInput={
                <TextField
                  fullWidth
                  label="Data Inicial"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              }
              dateFormat="dd/MM/yyyy"
              locale={pt}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              selected={filters.dateTo}
              onChange={(d) => handleFilterChange('dateTo', d)}
              customInput={<TextField fullWidth label="Data Final" />}
              dateFormat="dd/MM/yyyy"
              locale={pt}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Faturamento"
              type="number"
              fullWidth
              value={filters.faturamento}
              onChange={(e) => handleFilterChange('faturamento', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="CMV Meta (%)"
              type="number"
              fullWidth
              value={filters.cmvMeta}
              onChange={(e) => handleFilterChange('cmvMeta', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2} display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={fetchReport} fullWidth>
              Buscar
            </Button>
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              options={insumoOptions}
              loading={insumosLoading}
              onOpen={() => { /* prefetch leve */ }}
              getOptionLabel={(option) => option?.nome || ''}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
              onInputChange={(_, value) => setInsumosQuery(value)}
              onChange={(_, value) => handleFilterChange('insumo', value)}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filtrar por insumo"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {insumosLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  fullWidth
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* KPIs */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Estoque Inicial" value={summary?.estoqueInicial} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Entradas" value={summary?.entradas} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Estoque Final" value={summary?.estoqueFinal} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label="Saídas" value={summary?.saidas} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 2, borderRadius: 3, backdropFilter: 'blur(8px)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.6))',
              border: '1px solid', borderColor: 'divider'
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {acimaMeta === true && (
                <Tooltip title="Acima da meta">
                  <TrendingUp color="error" fontSize="small" />
                </Tooltip>
              )}
              {acimaMeta === false && (
                <Tooltip title="Abaixo da meta">
                  <TrendingDown color="success" fontSize="small" />
                </Tooltip>
              )}
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: .6 }}>
                CMV%
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {loading ? <Skeleton width={60} /> : formatPerc(summary?.cmvPercentual)}
            </Typography>
            {filters.cmvMeta && (
              <Typography variant="body2" color="text.secondary">
                Meta: {formatPerc(filters.cmvMeta)}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          background: '#fff'
        }}
      >
        <Table
          size="small"
          sx={{
            '& thead th': { bgcolor: 'grey.50', fontWeight: 600 },
            '& th, & td': { px: 2 },
            '& th:first-of-type, & td:first-of-type': { pl: 3 },
            '& th:last-of-type,  & td:last-of-type':  { pr: 3 },
            '& thead th:first-of-type': { borderTopLeftRadius: 12 },
            '& thead th:last-of-type':  { borderTopRightRadius: 12 },
            '& tbody tr:hover': { bgcolor: 'action.hover' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Estoque Inicial</TableCell>
              <TableCell>Entradas</TableCell>
              <TableCell>Saídas</TableCell>
              <TableCell>Estoque Final</TableCell>
              <TableCell>CMV%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AnimatePresence initial={false}>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell><Skeleton height={20} /></TableCell>
                    <TableCell><Skeleton height={20} width="60%" /></TableCell>
                    <TableCell><Skeleton height={20} width="60%" /></TableCell>
                    <TableCell><Skeleton height={20} width="60%" /></TableCell>
                    <TableCell><Skeleton height={20} width="60%" /></TableCell>
                    <TableCell><Skeleton height={20} width="40%" /></TableCell>
                  </TableRow>
                ))
              ) : displayedItems.length ? (
                displayedItems.map((it) => (
                  <TableRow
                    key={it.id || it.insumo_id}
                    component={motion.tr}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.15 }}
                  >
                    <TableCell>{it.nome || it.item || '—'}</TableCell>
                    <TableCell>{formatBRL(it.estoque_inicial)}</TableCell>
                    <TableCell>{formatBRL(it.entradas)}</TableCell>
                    <TableCell>{formatBRL(it.saidas)}</TableCell>
                    <TableCell>{formatBRL(it.estoque_final)}</TableCell>
                    <TableCell>{formatPerc(it.cmv_percentual)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Nenhum item encontrado</Typography>
                  </TableCell>
                </TableRow>
              )}
            </AnimatePresence>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Dashboard;
